import { WORK_PHASE_LABEL, type WorkItemDto, type WorkItemsDto, type WorkOverviewDto, type WorkPhase, type WorkSourceDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiUpload, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, ErrorText } from '../../ui';
import { FileDrop } from './FileDrop';

/** Tempo stimato in parole. */
export function etaText(seconds: number | null): string {
  if (seconds === null) return '';
  if (seconds < 60) return 'meno di un minuto';
  const m = Math.round(seconds / 60);
  if (m < 60) return `circa ${m} ${m === 1 ? 'minuto' : 'minuti'}`;
  const h = Math.round(m / 60);
  return h < 48 ? `circa ${h} ${h === 1 ? 'ora' : 'ore'}` : `circa ${Math.round(h / 24)} giorni`;
}

const n = (x: number) => x.toLocaleString('it-IT');
const UNIT: Record<WorkSourceDto['kind'], [string, string]> = {
  posta: ['messaggio', 'messaggi'],
  caricamento: ['file', 'file'],
  documenti: ['documento', 'documenti'],
  programma: ['sincronizzazione', 'sincronizzazioni'],
};

/** Barra sottile con la percentuale. */
export function Bar({ percent, tone = 'accent' }: { percent: number; tone?: 'accent' | 'green' | 'amber' }) {
  const color = tone === 'green' ? 'bg-emerald-500' : tone === 'amber' ? 'bg-amber-500' : 'bg-accent';
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-black/10" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
      <div className={`h-full ${color} transition-all duration-700`} style={{ width: `${Math.max(2, percent)}%` }} />
    </div>
  );
}

/**
 * Una fonte della coda di lavorazione (ADR 0043): barra, quanti elementi su quanti, in che fase sono, tempo stimato;
 * sotto, a richiesta, gli elementi uno per uno (file per file, messaggio per messaggio).
 */
export function SourceProgress({ source, title, startOpen = false }: { source: WorkSourceDto; title?: string; startOpen?: boolean }) {
  const [open, setOpen] = useState(startOpen);
  const { t, tenant } = useTenant();
  const queryClient = useQueryClient();
  const retry = useMutation({
    mutationFn: () => api(t(`/work/sources/${source.id}/retry`), { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] }),
  });
  const [one, many] = UNIT[source.kind];
  const running = source.status === 'in_corso';
  const phases = (Object.entries(source.phases) as Array<[WorkPhase, number]>).filter(([, v]) => v > 0);
  return (
    <div className="rounded-lg border border-black/10 bg-black/[0.015] p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{title ?? source.label}</span>
        {source.paused ? <Badge tone="amber">in pausa</Badge> : running ? <Badge tone="blue">in lavorazione</Badge> : <Badge tone="green">fatto</Badge>}
        {source.errors > 0 && <Badge tone="red">{source.errors} in errore</Badge>}
        <span className="flex-1" />
        <span className="text-xs text-ink/60">{source.percent}%</span>
      </div>
      <div className="mt-2">
        <Bar percent={source.percent} tone={running ? 'accent' : 'green'} />
      </div>
      <p className="mt-1.5 text-xs text-ink/60">
        {source.counting
          ? 'Sto contando quanti sono…'
          : running && source.reading?.fase === 'campi'
            ? 'Scelgo i campi da leggere per questa azienda…'
            : running && source.reading?.fase === 'lettura'
              ? `Leggo «${source.reading.risorsa}»${source.reading.risorse > 1 ? ` (lettura ${n(source.reading.risorse_lette + 1)} di ${n(source.reading.risorse)})` : ''}: ${n(source.reading.righe)} ${source.reading.righe === 1 ? 'riga letta' : 'righe lette'}`
              : `${n(source.done)} di ${n(source.total)} ${source.total === 1 ? one : many}`}
        {source.children.total > 0 && ` · ${n(source.children.done)} di ${n(source.children.total)} ${source.kind === 'posta' ? 'allegati' : 'file contenuti'}`}
        {running && !source.counting && !source.paused && (source.eta_seconds !== null ? ` · ${etaText(source.eta_seconds)} alla fine` : ' · calcolo il tempo che manca…')}
        {!running && source.kind === 'posta' && source.initial_done && ' · ora leggo solo i messaggi nuovi'}
      </p>
      {source.paused && <p className="mt-1 text-xs text-amber-800">{source.paused}</p>}
      {source.night && (
        <p className="mt-1 text-xs text-ink/60">
          {source.night.now
            ? `Di notte: leggo anche i ${n(source.night.items)} elementi più vecchi di ${source.night.days} giorni.`
            : `${n(source.night.items)} elementi più vecchi di ${source.night.days} giorni (messaggi e loro allegati) li leggo di notte, tra le ${source.night.from} e le ${source.night.to}.`}
        </p>
      )}
      {running && phases.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {phases.map(([p, v]) => (
            <span key={p} className="rounded bg-black/5 px-1.5 py-0.5 text-[11px] text-ink/60">
              {WORK_PHASE_LABEL[p]}: {n(v)}
            </span>
          ))}
        </div>
      )}
      <div className="mt-1 flex flex-wrap gap-1">
        <button type="button" className="text-xs text-accent underline-offset-2 hover:underline" onClick={() => setOpen((o) => !o)}>
          {open ? 'Nascondi il dettaglio' : source.kind === 'posta' ? 'Vedi messaggio per messaggio' : source.kind === 'programma' ? 'Vedi il dettaglio' : 'Vedi file per file'}
        </button>
        {source.errors > 0 && (
          <Button variant="ghost" className="!px-2 !py-0.5 text-xs" disabled={retry.isPending} onClick={() => retry.mutate()}>
            Riprova gli errori
          </Button>
        )}
      </div>
      {open && <SourceItems sourceId={source.id} live={running} />}
    </div>
  );
}

const ITEM_STATUS: Record<WorkItemDto['status'], { label: string; tone: 'gray' | 'blue' | 'green' | 'red' | 'amber' }> = {
  da_fare: { label: 'in coda', tone: 'gray' },
  in_corso: { label: 'in lavorazione', tone: 'blue' },
  fatto: { label: 'fatto', tone: 'green' },
  errore: { label: 'errore', tone: 'red' },
  saltato: { label: 'saltato', tone: 'amber' },
};

function SourceItems({ sourceId, live }: { sourceId: string; live: boolean }) {
  const { t, tenant } = useTenant();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<'tutti' | 'in_corso' | 'errori'>('tutti');
  const items = useTenantQuery<WorkItemsDto>(['work', sourceId, filter], `/work/sources/${sourceId}?filtro=${filter}&limite=300`, { refetchInterval: live ? 3000 : false });
  const retry = useMutation({
    mutationFn: (id: string) => api(t(`/work/items/${id}/retry`), { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] }),
  });
  const list = items.data?.items ?? [];
  return (
    <div className="mt-2">
      <div className="mb-1 flex gap-1 text-xs">
        {(['tutti', 'in_corso', 'errori'] as const).map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)} className={`rounded px-2 py-0.5 ${filter === f ? 'bg-black/10 text-ink' : 'text-ink/55 hover:bg-black/5'}`}>
            {f === 'tutti' ? 'Tutti' : f === 'in_corso' ? 'Da finire' : 'Errori'}
          </button>
        ))}
        <span className="flex-1" />
        {items.data && <span className="text-ink/45">{list.length < items.data.total ? `${list.length} di ${n(items.data.total)}` : n(items.data.total)}</span>}
      </div>
      <ErrorText>{items.error ? errorMessage(items.error) : retry.error ? errorMessage(retry.error) : ''}</ErrorText>
      <ul className="max-h-72 divide-y divide-black/5 overflow-auto rounded border border-black/10 bg-white">
        {list.map((i) => {
          const st = ITEM_STATUS[i.status];
          const rows = typeof i.progress.righe === 'number' ? (i.progress.righe as number) : null;
          return (
            <li key={i.id} className={`flex flex-wrap items-center gap-2 px-2 py-1.5 text-xs ${i.parent_id ? 'pl-6' : ''}`}>
              <span className="min-w-0 flex-1 truncate" title={i.label}>
                {i.parent_id && <span className="text-ink/35">↳ </span>}
                {i.label}
              </span>
              {i.status === 'in_corso' || i.status === 'da_fare' ? <span className="text-ink/45">{WORK_PHASE_LABEL[i.phase]}</span> : null}
              {rows !== null && <span className="text-ink/45">{n(rows)} righe{typeof i.progress.risorsa === 'string' && i.status === 'in_corso' ? ` · ${i.progress.risorsa}` : ''}</span>}
              <Badge tone={st.tone}>{st.label}</Badge>
              {(i.error || i.note) && <span className={`w-full truncate ${i.error ? 'text-red-700' : 'text-ink/45'}`} title={i.error ?? i.note ?? ''}>{i.error ?? i.note}</span>}
              {i.status === 'errore' && (
                <button type="button" className="text-accent hover:underline" disabled={retry.isPending} onClick={() => retry.mutate(i.id)}>
                  riprova
                </button>
              )}
            </li>
          );
        })}
        {!list.length && <li className="px-2 py-2 text-xs text-ink/45">{items.isLoading ? 'Carico…' : 'Niente da mostrare'}</li>}
      </ul>
    </div>
  );
}

/** Le fonti al lavoro dell'azienda (si aggiorna da sola finché c'è qualcosa in corso). */
export function useWork(enabled = true) {
  return useTenantQuery<WorkOverviewDto>(['work'], '/work', { enabled, refetchInterval: (d) => (d && d.active.sources > 0 ? 4000 : 20_000) });
}

const newBatch = (prefix: string) => `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

/** Invio di più file nella coda di lavorazione, con il lotto, i file inviati e la fonte da seguire. */
export function useQueuedUpload(prefix = 'archivio') {
  const { t, tenant } = useTenant();
  const queryClient = useQueryClient();
  const [batch, setBatch] = useState<{ id: string; total: number; sent: number; sourceId: string | null } | null>(null);
  const [error, setError] = useState('');
  const work = useWork(!!batch?.sourceId);
  const send = async (files: File[]) => {
    if (!files.length) return;
    const b = { id: newBatch(prefix), total: files.length, sent: 0, sourceId: null as string | null };
    setBatch({ ...b });
    setError('');
    for (const f of files) {
      try {
        const r = await apiUpload<{ fonte_id: string }>(t(`/archive/files?lotto=${b.id}&totale=${files.length}`), f);
        b.sourceId = r.fonte_id;
      } catch (e) {
        setError((x) => `${x ? `${x} · ` : ''}${f.name}: ${errorMessage(e)}`);
      }
      b.sent++;
      setBatch({ ...b });
    }
    void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
  };
  return {
    send,
    error,
    uploading: !!batch && batch.sent < batch.total,
    sent: batch?.sent ?? 0,
    total: batch?.total ?? 0,
    source: batch?.sourceId ? work.data?.sources.find((s) => s.id === batch.sourceId) : undefined,
  };
}

/**
 * Caricamento in coda di più file (anche ZIP): ogni file si conserva subito e la coda lo archivia, lo legge e lo mette
 * nell'indice; sotto la barra del lotto, file per file. `prefix` lega il lotto al suo passo (es. le fatture).
 */
export function QueuedUpload({ prefix = 'archivio', label, accept }: { prefix?: string; label?: string; accept?: string }) {
  const q = useQueuedUpload(prefix);
  return (
    <div className="space-y-3">
      <FileDrop
        multiple
        accept={accept ?? '*'}
        hint="Più file insieme, anche cartelle compresse (ZIP)"
        onFiles={(fs) => void q.send(fs)}
        disabled={q.uploading}
        label={q.uploading ? `Invio dei file: ${q.sent} di ${q.total}…` : label}
      />
      <UploadState q={q} />
    </div>
  );
}

/** Invio in corso e lavorazione del lotto. */
export function UploadState({ q }: { q: ReturnType<typeof useQueuedUpload> }) {
  return (
    <>
      {q.uploading && <Bar percent={Math.round((q.sent / Math.max(1, q.total)) * 100)} />}
      <ErrorText>{q.error}</ErrorText>
      {q.source && <SourceProgress source={q.source} title="Lavorazione dei file caricati" startOpen />}
    </>
  );
}
